import { Box, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";

import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import WebhookConfig from "../components/WebhookConfig";
import { IntegrationsMapping } from "../IntegrationsMapping";
import WorkatoConnectForm from "./WorkatoConnectForm";

const INTEGRATION_CODE_NAME = "workato";

const WorkatoIntegration = () => {
  const integration = IntegrationsMapping[INTEGRATION_CODE_NAME];
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    integration.codeName
  );
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerWorkatoIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerWorkatoIntegration(values.webhookURL.trim())
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success(`${integration.displayName} integrated successfully`);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? ""
            : `Once integrated, the Breeze platform will send alerts about new detections to the ${integration.displayName} webhook.`
        }
        image={logos.WorkatoLogo}
        iconBorderRadius={0}
        name={integration.displayName}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <WebhookConfig
              toolName={integration.codeName}
              toolDisplayName={integration.displayName}
            />
          ) : (
            <div>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              <GuidelineStepByStep>
                <GuidelineStep title="Create a webhook in Workato">
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open("https://www.workato.com/", "_blank");
                      }}
                      style={{ textTransform: "none" }}
                    >
                      Open Workato
                    </PrimaryButton>
                  </Box>
                  <Typography variant="body1">
                    Create a webhook integration in Workato to receive alerts
                    from Breeze. Once the webhook is created, copy the webhook
                    URL and add it in the form below.
                  </Typography>
                </GuidelineStep>
                <GuidelineStep id="connectWorkato" title="Connect Workato">
                  <Box width={700}>
                    <WorkatoConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default WorkatoIntegration;
